import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { functions, httpsCallable } from '../config/firebase';
import { Loader2, Check, Copy, RefreshCw } from 'lucide-react';
import CirclesLogo from '../components/CirclesLogo';

export default function JoinPage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  // Track in-flight request to prevent concurrent calls only
  const isFetching = useRef(false);

  // Detect platform once
  const platform = useMemo(() => {
    const ua = navigator.userAgent || navigator.vendor;
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    return 'desktop';
  }, []);

  const loadCircle = async (isRetry = false) => {
    if (isFetching.current) return;

    try {
      isFetching.current = true;
      setLoading(true);
      setError(null);

      const getCirclePublic = httpsCallable(functions, 'getCircleByInviteCodePublic');
      const result = await getCirclePublic({ inviteCode });

      setCircle(result.data);
    } catch (err) {
      console.error('[JoinPage] Error:', err.code, err.message);

      if (err.code === 'functions/not-found') {
        setError({ type: 'invalid', message: 'This invite code is invalid or has expired.' });
      } else if (err.code === 'functions/resource-exhausted') {
        setError({ type: 'retry', message: 'Too many requests. Please wait a moment.' });
      } else {
        setError({ type: 'retry', message: 'Could not load invite. Please try again.' });
      }
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  useEffect(() => {
    document.title = 'Circles — Join My Circle and Meet Other Groups Together';

    // Apple Smart App Banner — shows native "OPEN" / "GET" in Safari.
    // app-argument deep links into the app with the invite context.
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=6757444421, app-argument=https://getcircles.com/join/${inviteCode}`;
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [inviteCode]);

  useEffect(() => {
    if (!inviteCode || !/^[A-Z2-9]{8}$/.test(inviteCode)) {
      setError({ type: 'invalid', message: 'Invalid invite code format.' });
      setLoading(false);
      return;
    }

    loadCircle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode]);

  const handleRetry = () => loadCircle(true);


  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  // Error
  if (error || !circle) {
    const isInvalid = error?.type === 'invalid';

    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <div className="w-16 h-16 rounded-full bg-neutral-100 mx-auto mb-6 flex items-center justify-center">
            <span className="text-3xl">{isInvalid ? '😕' : '🔄'}</span>
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2 lowercase">
            {isInvalid ? 'invalid invite' : 'something went wrong'}
          </h1>
          <p className="text-neutral-500 mb-6 text-sm">
            {error?.message || 'Could not load invite.'}
          </p>
          <div className="flex flex-col gap-3">
            {!isInvalid && (
              <button
                onClick={handleRetry}
                className="w-full bg-neutral-900 text-white px-6 py-3 rounded-full font-medium lowercase flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              >
                <RefreshCw className="h-4 w-4" />
                try again
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className={`w-full px-6 py-3 rounded-full font-medium lowercase active:scale-[0.98] transition-all ${
                isInvalid
                  ? 'bg-neutral-900 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              go home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const storeUrl = platform === 'android'
    ? `https://play.google.com/store/apps/details?id=com.getcircles.circles&referrer=invite_${inviteCode}`
    : 'https://apps.apple.com/us/app/circles-meet-groups-together/id6757444421';

  const handleOpenInApp = () => {
    if (platform === 'android') {
      const playStoreUrl = `https://play.google.com/store/apps/details?id=com.getcircles.circles&referrer=invite_${inviteCode}`;
      const intentUrl = `intent://join/${inviteCode}#Intent;scheme=circles;package=com.getcircles.circles;S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`;
      window.location.href = intentUrl;
    } else {
      window.location.href = `circles://join/${inviteCode}`;
    }
  };

  return (
    <div className="min-h-screen bg-white text-neutral-900 flex flex-col">
      <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div style={{ filter: 'drop-shadow(1px 1px 0px rgba(0, 0, 0, 0.9))' }}>
            <CirclesLogo size={36} color="#cef7fc" />
          </div>
          <span className="text-2xl font-bold lowercase tracking-tight text-neutral-900 mb-1">circles</span>
        </div>

        {/* Header */}
        <div className="mb-5 sm:mb-6 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 lowercase tracking-tight text-neutral-900">
            join {circle.circle.name}
          </h1>
          <p className="text-neutral-500 text-sm sm:text-base">
            {circle.members.length} {circle.members.length === 1 ? 'member' : 'members'}
          </p>
        </div>

        {/* Members */}
        {circle.members && circle.members.length > 0 && (
          <div className="flex justify-center -space-x-2 mb-6">
            {circle.members.slice(0, 5).map((member, idx) => (
              <div key={idx} className="flex flex-col items-center">
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.displayName || 'Member'}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-neutral-100 items-center justify-center"
                  style={{ display: member.photo ? 'none' : 'flex' }}
                >
                  <span className="text-2xl font-semibold text-neutral-400">
                    {(member.displayName || 'M').charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3 mb-6">
          {/* Invite code */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(inviteCode);
              if (copiedTimer.current) clearTimeout(copiedTimer.current);
              setCopied(true);
              copiedTimer.current = setTimeout(() => setCopied(false), 3500);
            }}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white px-5 py-4 rounded-2xl font-medium lowercase active:scale-[0.98] transition-all text-center"
          >
            <p className="font-mono font-bold text-lg tracking-widest mb-1 uppercase">{inviteCode}</p>
            <p className="text-[12px] text-white/60 flex items-center justify-center gap-1.5">
              {copied ? (
                <><Check className="h-3.5 w-3.5 text-[#cef7fc]" /> copied! use this in the app</>
              ) : (
                <><Copy className="h-3.5 w-3.5" /> tap to copy your invite code</>
              )}
            </p>
          </button>

          {/* Get the app */}
          <a
            href={storeUrl}
            className="w-full border border-neutral-300 hover:bg-neutral-50 px-5 py-3.5 rounded-2xl font-medium lowercase active:scale-[0.98] transition-all flex items-center justify-center gap-2.5"
          >
            {platform === 'android' ? (
              <svg viewBox="4 4 16 16" className="w-4 h-4" fill="#171717">
                <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A5.84 5.84 0 0012 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31A5.983 5.983 0 006 7h12c0-2.21-1.2-4.15-2.97-5.18-.07-.04-.14-.09-.21-.13-.05-.03-.1-.05-.15-.08-.01 0-.01-.01-.02-.01a6.12 6.12 0 00-.12-.06V1.52zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 384 512" className="w-3.5 h-3.5" fill="#171717">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
            )}
            <p className="text-[15px] text-neutral-900">
              {platform === 'android' ? 'get it on google play' : 'download on the app store'}
            </p>
          </a>
        </div>

        {/* Already have it */}
        <div className="text-center">
          <button
            onClick={handleOpenInApp}
            className="text-neutral-400 hover:text-neutral-600 text-sm lowercase transition-colors"
          >
            already have circles? <span className="underline text-neutral-600">open in the app</span>
          </button>
        </div>
      </div>
    </div>
  );
}
